import * as React from 'react';
import { useParams } from 'react-router-dom';
import { GameEngine, GameUI } from 'hearts-game-ui';
import {
	GameEvent,
	getMatchStats,
	getRandomInt,
	getRoundStats,
	getScorePileName,
	Manager,
	RoundStage,
} from 'hearts-engine';
import { useTranslation } from 'react-i18next';

import { PageTitle } from './PageTitle';

import { useApplication } from '../AppContext';
import { MultiplayerGameManager } from '../services/MultiplayerGameManager';
import ComputerConfig from '../configs/ComputerConfig.js';
import LevelConfig from '../configs/LevelConfig.js';

function takeAllTricks(gameState, winner = 2) {
	const winningsName = getScorePileName(winner);

	for (const card of gameState.deck) {
		card.pile = winningsName;
	}

	gameState.roundStage = RoundStage.TrickEnd;
}

function getRandomAvatarURL() {
	return `AvatarVV_${getRandomInt(1, 233).toString().padStart(4, '0')}.png`;
}

function selectBotPlayers(level = 'Easy') {
	const botTemplates = [];

	//this could be a function that returns the player templates based on the level
	const levelBotNames = LevelConfig[level];

	//select 3 random bots from the level config
	if (levelBotNames && levelBotNames.length > 0) {
		const shuffledBotNames = levelBotNames.sort(() => 0.5 - Math.random());
		const selectedBotNames = shuffledBotNames.slice(0, 3);

		for (let i = 0; i < selectedBotNames.length; i++) {
			const botName = selectedBotNames[i];
			const botConfig = ComputerConfig.find((bot) => bot.name === botName);

			if (botConfig) {
				botTemplates.push(botConfig);
			} else {
				console.warn(`Bot config for ${botName} not found in ComputerConfig.`);
				// Fallback to a default bot if not found
				botTemplates.push({
					name: botName,
					avatar: getRandomAvatarURL(),
					algorithm: 'LowestBot',
					skill: 1,
					delay: 600,
				});
			}
		}
	} else {
		const bots = [
			{
				name: 'Bob',
				avatar: 'AvatarVV_0173.png',
				algorithm: 'MediumBot',
				skill: 2,
				delay: 600,
			},
			{
				name: 'Charlie',
				avatar: 'AvatarVV_0114.png',
				algorithm: 'LowestBot',
				skill: 1,
				delay: 600,
			},
			{
				name: 'Dave',
				avatar: 'AvatarVV_0182.png',
				algorithm: 'LowestBot',
				skill: 1,
				delay: 600,
			},
			{
				name: 'Eve',
				avatar: 'AvatarVV_0013.png',
				algorithm: 'LowestBot',
				skill: 1,
				delay: 600,
			},
		];
		const shuffledBots = bots.sort(() => 0.5 - Math.random());
		const selectedBots = shuffledBots.slice(0, 3);
		botTemplates.push(...selectedBots);
	}

	return botTemplates;
}

const GameController = () => {
	const app = useApplication();
	const userdata = app.userdata || {};
	const [gameInstance, setGameInstance] = React.useState(null);
	const { t } = useTranslation();
	const { level, mode } = useParams();
	const [ready, setReady] = React.useState(false);
	const gameRef = React.useRef(null);
	const expectiminimaxWorkerRef = React.useRef(null);

	// Remove this effect since we'll handle audio in PageTitle
	// React.useEffect(() => {
	// 	gameRef.current?.setAudioEnabled?.(!userdata?.isMuted);
	// }, [userdata?.isMuted]);

	//https://www.devauthority.com/react/using-pixi-js-with-react-functional-components-hooks/
	//https://stackoverflow.com/questions/75710343/react-18-and-pixijs-7
	React.useEffect(() => {
		if (gameRef.current) {
			return;
		}

		const isOnline = mode === 'Online';
		let gameManager = null;
		let localPlayer = 2;

		const allBots = mode === 'Test';
		const isDebug = app.debug || mode === 'Test';

		const isContinue = mode === 'Continue';

		let room;
		let keyListener;

		(async () => {

			app.portal.loadStart();

			if (isOnline) {
				room = app.multiplayer.getRoomById(level);

				if (room?.state) {
					gameManager = new MultiplayerGameManager(room);
					const players = room.state.players.toJSON();
					const playerInfo = players[room.sessionId];

					if (!playerInfo) {
						throw new Error(`Player ${room.sessionId} not in players object: "${JSON.stringify(players)}"`);
					}

					localPlayer = playerInfo.position ?? 2;
				}
			} else {

				gameManager = new Manager();

				if (isContinue) {
					const gameState = await app.getSavedGameState();
					gameManager.loadGameState(gameState);

				}

				if (!isContinue) {
					gameManager.createNewGame();
					gameManager.gameState.level = level;

					const playerTemplates = [];

					//human player
					const humanPlayer = {
						name: userdata?.name ?? 'Player',
						avatar: userdata?.avatar ?? getRandomAvatarURL(),
						algorithm: 'Human',
						isLocal: true,
						delay: 600,
						skill: '∞',
					};
					playerTemplates.push(humanPlayer);

					const botTemplates = selectBotPlayers(level);
					playerTemplates.push(...botTemplates);

					//assign positions
					for (let i = 0; i < playerTemplates.length; i++) {
						playerTemplates[i].position = i;
					}

					gameManager.gameState.players = playerTemplates;
				}

				//create users from players in game state
				const players = gameManager.gameState.players || [];

				const users = [];

				//switch mode to all bots and auto advance
				if (mode === 'Test') {
					for (const player of players) {
						if (player.algorithm === 'Human') {
							player.algorithm = 'MediumBot';
						}

						player.autoAdvanceRound = true;
						player.delay = 60;
					}
				}

				//Nadia (ExpectiminimaxBot) searches off-thread so the UI never blocks.
				let expectiminimaxWorker = null;

				if (players.some((player) => player.algorithm === 'ExpectiminimaxBot')) {
					expectiminimaxWorker = new Worker(new URL('../workers/expectiminimaxWorker.js', import.meta.url), {
						type: 'module',
					});
					expectiminimaxWorkerRef.current = expectiminimaxWorker;
				}

				for (let i = 0; i < players.length; i++) {
					const player = players[i];
					users.push(GameEngine.createAgent({
						algorithm: player.algorithm ?? 'LowestBot',
						autoAdvanceRound: player.autoAdvanceRound,
						position: i,
						delay: player.delay,
						name: player.name,
						avatar: player.avatar,
						skill: player.skill,
						searchWorker: player.algorithm === 'ExpectiminimaxBot' ? expectiminimaxWorker : undefined,
					}));
				}

				gameManager.users = users;

				//get local player position
				localPlayer = gameManager.gameState.players.findIndex((p) => p.isLocal);

				//todo: this is not working, the level is disappearing from state on continue
				//function handleNewGame is overwriting it.  The way "players" stay is because the manager is holding onto them.
				//should manager hold onto level?
				//ideally the game engine would have a createNewGame function that would take level and player info
				//right now the players are held by the manager, and when a new game is created the player are kept and then readded to the game state.
				//? how to have the players in the gamestate, not externally on the manager?
				//maybe a looser coupling where game send events to the stateless computers
				//how does the human player fit in, just computer that does not have an algorithm?

			}

			const gameUI = new GameUI({
				debug: isDebug,
				allBots,
				audioEnabled: !app?.userdata?.isMuted,
				translate: t,
				gameManager,
				localPlayer,
			});

			if (isDebug) {
				console.info('creating game');
			}

			gameUI.create('game-canvas1', null);

			if (isDebug) {
				console.info('gameUI is', gameUI);
			}

			gameUI.game.events.on('READY', setReady);

			if (!isContinue) {
				gameUI.startGame();
			} else {
				gameUI.gameScene.updateLayout();
			}

			app.portal.loadStop();
			app.portal.gameStart();

			gameRef.current = gameUI;
			setGameInstance(gameUI); // Add this line to update the state

			gameUI.gameScene.gameManager.on(GameEvent.StateChanged, (newState) => {
				if (!isOnline) {
					app?.saveGameState?.(newState);
				}

				if (newState.roundStage === RoundStage.RoundEnd) {
					saveRoundStats(newState, gameUI.options.localPlayer);
				}

				if (newState.roundStage === RoundStage.GameOver) {
					if (isDebug) {
						console.info('game is over!', newState, gameUI.options.localPlayer);
					}

					saveMatchStats(newState, gameUI.options.localPlayer);
					app?.saveGameState?.('');
				}
			});

			if (isOnline && room) {
				room.onMessage('restart', () => {
					gameUI?.gameScene?.roundScoreBoard?.reset?.();
				});
			}

			keyListener = (e) => {
				if (isDebug) {
					if (e.key === 'c') {
						takeAllTricks(gameUI.gameScene.gameManager.gameState, gameUI.options.localPlayer);
						gameUI.gameScene.onStateChanged();
					}

					if (e.key === 'r') {
						gameUI.gameScene.gameManager.gameState.players[2].name = `Super awesome! ${getRandomInt(0, 100)}`;
						gameUI.gameScene.gameManager.gameState.players[2].avatar = getRandomAvatarURL();
						gameUI.gameScene.onStateChanged();
					}
				}
			};

			window.addEventListener('keyup', keyListener);
		})();

		return () => {
			setReady(false);
			setGameInstance(null); // Reset game instance state

			gameRef.current?.game?.destroy(true);
			gameRef.current = null;
			expectiminimaxWorkerRef.current?.terminate?.();
			expectiminimaxWorkerRef.current = null;
			window.removeEventListener('keyup', keyListener);
			app.portal.gameStop();

			room?.leave?.();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	//save state function
	const saveRoundStats = (gameState, position) => {

		const ud = app.userdata || {};
		const statistics = getRoundStats(gameState, position, ud.stats);

		//merger statistics into user data
		ud.stats = statistics;
		app.save();

		//check for achievements
		app.achievements.checkForAchievementAndReward(ud, ud);
	};

	const saveMatchStats = (gameState, position) => {

		const ud = app.userdata || {};
		const statistics = getMatchStats(gameState, position, ud.stats);

		//merger statistics into user data
		ud.stats = statistics;
		app.updateLevel();
		app.save();

		//check for achievements
		app.achievements.checkForAchievementAndReward(ud, ud);
	};

	return (
		<div id="Game" className="page noAnimate activePage">
			<div className="gamePageWrapper" >
				<PageTitle className="title-separator" shouldShowVolume game={gameInstance} />
				<div id="game-canvas1" className="gameDiv flex-1-1-auto" style={{
					visible: !!ready,
				}} />
			</div>
		</div>
	);
};

export default React.memo(GameController);
